import React, { useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  rectIntersection,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor,
} from '@dnd-kit/core';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { logBitacora } from '../utils/bitacora';
import { useUser } from '../components/UserContext';
import { ChevronDownIcon, ChevronRightIcon, CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon, PauseCircleIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { EVENTO_TIPO_LABELS, ENDOSO_SOLICITANTE_LABELS } from '../utils/fianzaEventos';
import {
  PENDIENTE_TIPO_LABELS,
  contarPendientes,
  ordenarPorUrgencia,
  pendienteFromEndoso,
} from '../utils/pendientes';
import { usePendientesDecorados } from '../hooks/usePendientes';
import { usePulsoData, DOC_LABELS } from '../hooks/usePulsoData';
import PipelineColumn from '../components/pulso/PipelineColumn';
import PipelineCard from '../components/pulso/PipelineCard';
import { ContratoView } from '../components/ContratoView';
import { FianzaView } from '../components/FianzaView';

// Case- and accent-insensitive matching, igual que en ConvenioDetails.
const normalizar = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

// Cada pestaña trae objetos de forma distinta (cliente, fianza, requisito,
// endoso), así que en vez de un predicado por pestaña se barre esta lista y se
// ignora el campo que no exista en el objeto.
const CAMPOS_BUSQUEDA = [
  'clientName',
  'convenioNombre',
  'beneficiarioNombre',
  'fiadoNombre',
  'nombreEmpresa',
  'rfc',
  'contractTitle',
  'contractNumero',
  'numeroContrato',
  'numeroFianza',
  'tipo',
  'tipoFianza',
  'afianzadora',
  'beneficiario',
  'beneficiarioNombre',
  'proyectoNombre',
  'statusReason',
  'descripcion',
];

const TAB_LIST = [
  { key: 'clientes', label: 'Clientes' },
  { key: 'pipeline', label: 'Pipeline' },
  { key: 'diferidas', label: 'Diferidas' },
  { key: 'despues_termino', label: 'Después del Término' },
  { key: 'vencimientos', label: 'Vencimientos' },
  { key: 'pendientes', label: 'Pendientes' },
];

/**
 * Pipeline column configuration
 */
const PIPELINE_COLUMNS = [
  { id: 'pendientes', title: 'Sin iniciar', subtitle: 'Requisitos sin trámite' },
  { id: 'en_proceso', title: 'En Proceso', subtitle: 'Fianzas en trámite' },
  { id: 'emitidas_sin_pagar', title: 'Emitidas sin Pagar', subtitle: 'Pendientes de pago' },
  { id: 'pagadas', title: 'Pagadas', subtitle: 'Completadas' },
];

/**
 * Pulso - Business Pulse Dashboard
 */
const Pulso = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => {
    const requested = new URLSearchParams(location.search).get('tab');
    // The tab used to be endoso-only; keep old links working.
    const resolved = requested === 'endosos_pendientes' ? 'pendientes' : requested;
    return TAB_LIST.some((t) => t.key === resolved) ? resolved : 'pipeline';
  });
  const [search, setSearch] = useState('');
  const [activeCard, setActiveCard] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [clientFilter, setClientFilter] = useState(null);
  const [expandedClients, setExpandedClients] = useState(new Set());
  const [expandedDespuesTermino, setExpandedDespuesTermino] = useState(new Set());
  const [expirationFilters, setExpirationFilters] = useState({
    vencidas: false,
    under30: true,
    under60: true,
    under90: true,
    over90: true,
  });
  const [showRenovableOnly, setShowRenovableOnly] = useState(false);
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all', 'clientes', 'convenios'
  const [selectedFianza, setSelectedFianza] = useState(null);
  const [dragMessage, setDragMessage] = useState(null);
  const [pendingMoves, setPendingMoves] = useState({}); // { itemId: targetColumnId }
  const [postergarItem, setDiferirItem] = useState(null); // Item being postponed
  const [enProcesoItem, setEnProcesoItem] = useState(null); // Item being moved to en_proceso
  const [enProcesoAfianzadoras, setEnProcesoAfianzadoras] = useState([{ nombre: '', comentarios: '' }]);
  const [editEnProcesoItem, setEditEnProcesoItem] = useState(null); // Item being edited

  const {
    pipeline,
    expirations,
    clientStatus,
    despuesTermino,
    endososPendientes,
    clientCounts,
    expirationCounts,
    pipelineCounts,
    despuesTerminoCounts,
    clientsById,
    contractsById,
    loading,
  } = usePulsoData();

  // The pendientes collection. Its own listener rather than a DataContext pool:
  // the rules grant brokers the whole org in one branch, so there is no
  // predicate split to model here.
  const { abiertos: pendientesAbiertos } = usePendientesDecorados();

  const { user, userData } = useUser();

  const searchTerm = normalizar(search.trim());
  const matchesSearch = useCallback(
    (item) => {
      if (!searchTerm) return true;
      return CAMPOS_BUSQUEDA.some((campo) => {
        const raw = item?.[campo];
        if (raw == null) return false;
        // Varios campos del contrato vienen como { value, ... }.
        const texto = typeof raw === 'object' ? raw.value : raw;
        return normalizar(texto).includes(searchTerm);
      });
    },
    [searchTerm]
  );

  // Get filtered clients based on selected filter
  const getFilteredClients = useCallback(() => {
    if (!clientStatus) return [];
    const base = !clientFilter
      ? [
          // Return all clients sorted by status priority
          ...(clientStatus.vencidos || []),
          ...(clientStatus.porVencer || []),
          ...(clientStatus.incompletos || []),
          ...(clientStatus.ok || []),
        ]
      : clientStatus[clientFilter] || [];
    return base.filter(matchesSearch);
  }, [clientFilter, clientStatus, matchesSearch]);

  // Toggle client row expansion
  const toggleClientExpanded = useCallback((clientId, e) => {
    e.stopPropagation();
    setExpandedClients(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  }, []);

  // Toggle despues termino row expansion
  const toggleDespuesTerminoExpanded = useCallback((itemId, e) => {
    e.stopPropagation();
    setExpandedDespuesTermino(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }, []);

  // Get document status for a client
  const getDocStatus = useCallback((client, docType) => {
    if (client.expiredDocs?.includes(docType)) return 'expired';
    if (client.expiringSoonDocs?.includes(docType)) return 'expiring';
    if (client.uploadedDocs?.includes(docType)) return 'ok';
    return 'missing';
  }, []);

  // Apply optimistic moves to pipeline for immediate UI feedback
  const optimisticPipeline = useMemo(() => {
    if (Object.keys(pendingMoves).length === 0) return pipeline;

    // Deep clone the pipeline
    const result = {
      pendientes: [...(pipeline.pendientes || [])],
      en_proceso: [...(pipeline.en_proceso || [])],
      emitidas_sin_pagar: [...(pipeline.emitidas_sin_pagar || [])],
      pagadas: [...(pipeline.pagadas || [])],
    };

    // Apply pending moves
    Object.entries(pendingMoves).forEach(([itemId, targetColumn]) => {
      // Find and remove item from its current column
      let movedItem = null;
      for (const columnId of Object.keys(result)) {
        const index = result[columnId].findIndex(item => item.id === itemId);
        if (index !== -1) {
          [movedItem] = result[columnId].splice(index, 1);
          break;
        }
      }
      // Add to target column
      if (movedItem && result[targetColumn]) {
        result[targetColumn].push(movedItem);
      }
    });

    return result;
  }, [pipeline, pendingMoves]);

  // Apply source filter (all/clientes/convenios)
  const filteredPipeline = useMemo(() => {
    const bySource = sourceFilter === 'all'
      ? () => true
      : sourceFilter === 'convenios'
      ? (item) => item.isConvenio
      : (item) => !item.isConvenio;
    const filterFn = (item) => bySource(item) && matchesSearch(item);
    return {
      pendientes: (optimisticPipeline.pendientes || []).filter(filterFn),
      en_proceso: (optimisticPipeline.en_proceso || []).filter(filterFn),
      emitidas_sin_pagar: (optimisticPipeline.emitidas_sin_pagar || []).filter(filterFn),
      pagadas: (optimisticPipeline.pagadas || []).filter(filterFn),
    };
  }, [optimisticPipeline, sourceFilter, matchesSearch]);

  // Las demás pestañas consumen su arreglo directo, así que la búsqueda se
  // aplica aquí una vez en lugar de en cada punto de render.
  const diferidasFiltradas = useMemo(
    () => (pipeline.diferidas || []).filter(matchesSearch),
    [pipeline.diferidas, matchesSearch]
  );
  const despuesTerminoFiltrado = useMemo(
    () => (despuesTermino || []).filter(matchesSearch),
    [despuesTermino, matchesSearch]
  );
  const pendientesFiltrados = useMemo(() => {
    const merged = [
      ...(pendientesAbiertos || []),
      ...(endososPendientes || []).map(pendienteFromEndoso),
    ].map((row) => ({
      ...row,
      // Pulso opens contracts by contractId; the pendientes collection stores
      // contratoId. Normalize so one click handler serves both origins.
      contractId: row.contractId || row.contratoId || null,
      clientId: row.clientId || row.clienteId || null,
    }));
    return ordenarPorUrgencia(merged.filter(matchesSearch));
  }, [pendientesAbiertos, endososPendientes, matchesSearch]);

  const pendientesCounts = useMemo(
    () => contarPendientes(pendientesFiltrados),
    [pendientesFiltrados]
  );

  // Toggle expiration filter
  const toggleExpirationFilter = useCallback((key) => {
    setExpirationFilters(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Get filtered expirations based on selected filters
  const getFilteredExpirations = useCallback(() => {
    let result = [];
    if (expirationFilters.vencidas) result.push(...(expirations.vencidas || []));
    if (expirationFilters.under30) result.push(...(expirations.under30 || []));
    if (expirationFilters.under60) result.push(...(expirations.under60 || []));
    if (expirationFilters.under90) result.push(...(expirations.under90 || []));
    if (expirationFilters.over90) result.push(...(expirations.over90 || []));

    // Filter by renovable if enabled
    if (showRenovableOnly) {
      result = result.filter(fianza => fianza.renovable);
    }

    return result.filter(matchesSearch);
  }, [expirations, expirationFilters, showRenovableOnly, matchesSearch]);

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  );

  // Custom collision detection
  const collisionDetection = useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }
    return rectIntersection(args);
  }, []);

  // Find which column an item belongs to
  const findColumnForItem = useCallback((itemId) => {
    for (const [columnId, items] of Object.entries(pipeline)) {
      if (items.some(item => item.id === itemId)) {
        return columnId;
      }
    }
    return null;
  }, [pipeline]);

  // Find an item by ID across all columns
  const findItem = useCallback((itemId) => {
    for (const items of Object.values(pipeline)) {
      const item = items.find(i => i.id === itemId);
      if (item) return item;
    }
    return null;
  }, [pipeline]);

  // Handle drag start
  const handleDragStart = (event) => {
    const { active } = event;
    const item = findItem(active.id);
    if (item) {
      setActiveCard(item);
    }
  };

  // Handle drag end - update fianza/fianzaRequerida status
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveCard(null);

    if (!over) return;

    const itemId = active.id;
    const overId = over.id;

    // Determine target column
    let targetColumnId = overId;

    // If dropped on a card, find its column
    if (!PIPELINE_COLUMNS.some(col => col.id === overId)) {
      targetColumnId = findColumnForItem(overId);
    }

    if (!targetColumnId) return;

    // Find current column
    const currentColumnId = findColumnForItem(itemId);

    // If same column, do nothing
    if (currentColumnId === targetColumnId) return;

    // Get the item being dragged
    const item = findItem(itemId);
    if (!item) return;

    // Helper to show temporary message
    const showMessage = (msg) => {
      setDragMessage(msg);
      setTimeout(() => setDragMessage(null), 3000);
    };

    // Handle transitions based on item type
    try {
      if (item.type === 'fianzaRequerida') {
        // FianzaRequerida can only move between pendientes and en_proceso
        if (currentColumnId === 'pendientes' && targetColumnId === 'en_proceso') {
          // Show dialog to capture afianzadoras and comments
          setEnProcesoItem(item);
          setEnProcesoAfianzadoras([]);
        } else if (currentColumnId === 'en_proceso' && targetColumnId === 'pendientes') {
          // Moving back to pendientes - clear afianzadoras and comentarios
          setPendingMoves(prev => ({ ...prev, [itemId]: targetColumnId }));

          try {
            const contractRef = doc(db, 'contracts', item.contractId);
            const contractSnap = await getDoc(contractRef);

            if (contractSnap.exists()) {
              const contractData = contractSnap.data();
              const fianzasRequeridas = contractData.fianzasRequeridas?.value || [];

              const reqIndex = parseInt(item.id.split('-req-')[1]);
              if (fianzasRequeridas[reqIndex]) {
                fianzasRequeridas[reqIndex].estatus = 'pendiente';
                delete fianzasRequeridas[reqIndex].afianzadoras;
                delete fianzasRequeridas[reqIndex].comentariosEnProceso;

                await updateDoc(contractRef, {
                  'fianzasRequeridas.value': fianzasRequeridas,
                  updatedAt: Date.now(),
                });

                // Log to bitacora
                await logBitacora({
                  action: 'UPDATE',
                  collection: 'contracts',
                  documentId: item.contractId,
                  changes: [{
                    field: 'fianzasRequeridas.estatus',
                    oldValue: 'en_proceso',
                    newValue: 'pendiente',
                  }],
                  metadata: {
                    clientId: item.clientId,
                    clientName: item.clientName,
                    contratoId: item.contractId,
                    description: `Regresó fianza '${item.tipo || 'Sin tipo'}' a pendientes para '${item.clientName}'`,
                  },
                  user,
                  userData,
                });
              }
            }
          } finally {
            setPendingMoves(prev => {
              const next = { ...prev };
              delete next[itemId];
              return next;
            });
          }
        } else if (targetColumnId === 'emitidas_sin_pagar' || targetColumnId === 'pagadas') {
          // Trying to move fianzaRequerida to emitidas columns
          showMessage('Para emitir una fianza, abre el contrato y sube la fianza desde ahí.');
        }
      } else if (item.type === 'fianza') {
        // Fianzas cannot be moved between columns via drag-drop
        if (targetColumnId === 'pagadas') {
          showMessage('Para marcar como pagada, abre la fianza y sube el comprobante de pago.');
        } else if (currentColumnId === 'pagadas' && targetColumnId === 'emitidas_sin_pagar') {
          showMessage('Las fianzas pagadas no pueden regresar a emitidas sin pagar.');
        } else if ((currentColumnId === 'emitidas_sin_pagar') &&
                   (targetColumnId === 'pendientes' || targetColumnId === 'en_proceso')) {
          showMessage('Las fianzas emitidas no pueden regresar a pendientes o en proceso.');
        }
      }
    } catch (err) {
      console.error('Error updating item status:', err);
    }
  };

  // Handle drag cancel
  const handleDragCancel = () => {
    setActiveCard(null);
  };

  // Handle card click - open ContratoView for the related contract
  const handleCardClick = (item) => {
    // Item contains contractId, use it to show the contract view
    setSelectedContract(item);
  };

  // Handle postergar - open confirmation dialog
  const handleDiferir = useCallback((item) => {
    setDiferirItem(item);
  }, []);

  // Handle postergar confirmation
  const handleDiferirConfirm = useCallback(async () => {
    if (!postergarItem) return;

    try {
      const contractRef = doc(db, 'contracts', postergarItem.contractId);
      const contractSnap = await getDoc(contractRef);

      if (contractSnap.exists()) {
        const contractData = contractSnap.data();
        const fianzasRequeridas = contractData.fianzasRequeridas?.value || [];

        // Find the index of this fianzaRequerida
        const reqIndex = parseInt(postergarItem.id.split('-req-')[1]);
        if (fianzasRequeridas[reqIndex]) {
          const oldEstatus = fianzasRequeridas[reqIndex].estatus || 'pendiente';
          fianzasRequeridas[reqIndex].estatus = 'diferida';

          await updateDoc(contractRef, {
            'fianzasRequeridas.value': fianzasRequeridas,
            updatedAt: Date.now(),
          });

          // Log to bitacora
          await logBitacora({
            action: 'UPDATE',
            collection: 'contracts',
            documentId: postergarItem.contractId,
            changes: [{
              field: 'fianzasRequeridas.estatus',
              oldValue: oldEstatus,
              newValue: 'diferida',
            }],
            metadata: {
              clientId: postergarItem.clientId,
              clientName: postergarItem.clientName,
              contratoId: postergarItem.contractId,
              description: `Difirió fianza '${postergarItem.tipo || 'Sin tipo'}' para '${postergarItem.clientName}'`,
            },
            user,
            userData,
          });
        }
      }
    } catch (err) {
      console.error('Error updating diferida status:', err);
    }

    setDiferirItem(null);
  }, [postergarItem, user, userData]);

  // Handle en proceso confirmation
  const handleEnProcesoConfirm = useCallback(async () => {
    if (!enProcesoItem) return;

    // Filter out empty afianzadoras
    const validAfianzadoras = enProcesoAfianzadoras.filter(a => a.nombre.trim());

    // Capture item data before closing dialog
    const itemToUpdate = enProcesoItem;

    // Close dialog immediately (optimistic)
    setEnProcesoItem(null);
    setEnProcesoAfianzadoras([]);

    // Optimistic update for pipeline UI
    setPendingMoves(prev => ({ ...prev, [itemToUpdate.id]: 'en_proceso' }));

    try {
      const contractRef = doc(db, 'contracts', itemToUpdate.contractId);
      const contractSnap = await getDoc(contractRef);

      if (contractSnap.exists()) {
        const contractData = contractSnap.data();
        const fianzasRequeridas = contractData.fianzasRequeridas?.value || [];

        const reqIndex = parseInt(itemToUpdate.id.split('-req-')[1]);
        if (fianzasRequeridas[reqIndex]) {
          fianzasRequeridas[reqIndex].estatus = 'en_proceso';
          fianzasRequeridas[reqIndex].afianzadoras = validAfianzadoras;

          await updateDoc(contractRef, {
            'fianzasRequeridas.value': fianzasRequeridas,
            updatedAt: Date.now(),
          });

          // Log to bitacora - include afianzadoras details in description
          const afianzadorasDetails = validAfianzadoras.map(a => {
            let detail = a.nombre;
            if (a.comentarios) detail += ` (${a.comentarios})`;
            return detail;
          }).join(', ');

          const description = validAfianzadoras.length > 0
            ? `Inició proceso de fianza '${itemToUpdate.tipo || 'Sin tipo'}' para '${itemToUpdate.clientName}' con afianzadoras: ${afianzadorasDetails}`
            : `Inició proceso de fianza '${itemToUpdate.tipo || 'Sin tipo'}' para '${itemToUpdate.clientName}'`;

          await logBitacora({
            action: 'UPDATE',
            collection: 'contracts',
            documentId: itemToUpdate.contractId,
            changes: [{
              field: 'fianzasRequeridas.estatus',
              oldValue: 'pendiente',
              newValue: 'en_proceso',
            }],
            metadata: {
              clientId: itemToUpdate.clientId,
              clientName: itemToUpdate.clientName,
              contratoId: itemToUpdate.contractId,
              description,
            },
            user,
            userData,
          });
        }
      }
    } catch (err) {
      console.error('Error updating en_proceso status:', err);
    } finally {
      setPendingMoves(prev => {
        const next = { ...prev };
        delete next[itemToUpdate.id];
        return next;
      });
    }
  }, [enProcesoItem, enProcesoAfianzadoras, user, userData]);

  // Handle edit en proceso - open edit dialog with existing data
  const handleEditEnProceso = useCallback((item) => {
    setEditEnProcesoItem(item);
    // Pre-fill with existing afianzadoras or default to one empty entry
    const existingAfianzadoras = item.afianzadoras && Array.isArray(item.afianzadoras) && item.afianzadoras.length > 0
      ? item.afianzadoras
      : [{ nombre: '', comentarios: '' }];
    setEnProcesoAfianzadoras(existingAfianzadoras);
  }, []);

  // Handle edit en proceso confirmation
  const handleEditEnProcesoConfirm = useCallback(async () => {
    if (!editEnProcesoItem) return;

    // Filter out empty afianzadoras
    const validAfianzadoras = enProcesoAfianzadoras.filter(a => a.nombre.trim());

    // Capture item data before closing dialog
    const itemToUpdate = editEnProcesoItem;
    const oldAfianzadoras = itemToUpdate.afianzadoras || [];

    // Close dialog immediately (optimistic)
    setEditEnProcesoItem(null);
    setEnProcesoAfianzadoras([]);

    try {
      const contractRef = doc(db, 'contracts', itemToUpdate.contractId);
      const contractSnap = await getDoc(contractRef);

      if (contractSnap.exists()) {
        const contractData = contractSnap.data();
        const fianzasRequeridas = contractData.fianzasRequeridas?.value || [];

        const reqIndex = parseInt(itemToUpdate.id.split('-req-')[1]);
        if (fianzasRequeridas[reqIndex]) {
          fianzasRequeridas[reqIndex].afianzadoras = validAfianzadoras;

          await updateDoc(contractRef, {
            'fianzasRequeridas.value': fianzasRequeridas,
            updatedAt: Date.now(),
          });

          // Log to bitacora - include afianzadoras details in description
          const afianzadorasDetails = validAfianzadoras.map(a => {
            let detail = a.nombre;
            if (a.comentarios) detail += ` (${a.comentarios})`;
            return detail;
          }).join(', ');

          const description = validAfianzadoras.length > 0
            ? `Actualizó afianzadoras de fianza '${itemToUpdate.tipo || 'Sin tipo'}' para '${itemToUpdate.clientName}': ${afianzadorasDetails}`
            : `Eliminó afianzadoras de fianza '${itemToUpdate.tipo || 'Sin tipo'}' para '${itemToUpdate.clientName}'`;

          await logBitacora({
            action: 'UPDATE',
            collection: 'contracts',
            documentId: itemToUpdate.contractId,
            changes: [{
              field: 'fianzasRequeridas.afianzadoras',
              oldValue: oldAfianzadoras,
              newValue: validAfianzadoras,
            }],
            metadata: {
              clientId: itemToUpdate.clientId,
              clientName: itemToUpdate.clientName,
              contratoId: itemToUpdate.contractId,
              description,
            },
            user,
            userData,
          });
        }
      }
    } catch (err) {
      console.error('Error updating en_proceso afianzadoras:', err);
    }
  }, [editEnProcesoItem, enProcesoAfianzadoras, user, userData]);

  // Handle reactivar - move back to pendientes
  const handleReactivar = useCallback(async (item) => {
    try {
      const contractRef = doc(db, 'contracts', item.contractId);
      const contractSnap = await getDoc(contractRef);

      if (contractSnap.exists()) {
        const contractData = contractSnap.data();
        const fianzasRequeridas = contractData.fianzasRequeridas?.value || [];

        // Find the index of this fianzaRequerida
        const reqIndex = parseInt(item.id.split('-req-')[1]);
        if (fianzasRequeridas[reqIndex]) {
          fianzasRequeridas[reqIndex].estatus = 'pendiente';
          delete fianzasRequeridas[reqIndex].diferidaHasta;

          await updateDoc(contractRef, {
            'fianzasRequeridas.value': fianzasRequeridas,
            updatedAt: Date.now(),
          });

          // Log to bitacora
          await logBitacora({
            action: 'UPDATE',
            collection: 'contracts',
            documentId: item.contractId,
            changes: [{
              field: 'fianzasRequeridas.estatus',
              oldValue: 'diferida',
              newValue: 'pendiente',
            }],
            metadata: {
              clientId: item.clientId,
              clientName: item.clientName,
              contratoId: item.contractId,
              description: `Reactivó fianza '${item.tipo || 'Sin tipo'}' para '${item.clientName}'`,
            },
            user,
            userData,
          });
        }
      }
    } catch (err) {
      console.error('Error reactivating fianzaRequerida:', err);
    }
  }, [user, userData]);

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Toast message for drag restrictions */}
      {dragMessage && createPortal(
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] bg-amber-100 border border-amber-300 text-amber-800 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2">
          <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm font-medium">{dragMessage}</span>
        </div>,
        document.body
      )}

      {/* Header */}
      <div>
        <Heading level={1} className="text-2xl font-bold text-azul-surelia mb-1">
          Pulso
        </Heading>
      </div>

      {/* Tabs Navigation */}
      <div className="overflow-x-auto border-b border-azul-surelia/20">
        <div className="flex gap-1 min-w-max">
          {TAB_LIST.map((tab) => (
            <button
              key={tab.key}
              className={`px-3 sm:px-6 py-3 font-semibold focus:outline-none transition-all duration-200 border-b-2 whitespace-nowrap text-sm sm:text-base ${
                activeTab === tab.key
                  ? 'text-azul-surelia border-azul-surelia bg-azul-surelia/5'
                  : 'text-gray-500 border-transparent hover:text-azul-surelia hover:border-azul-surelia/40 hover:bg-azul-surelia/5'
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              {tab.key === 'pendientes' && pendientesCounts.total > 0 && (
                <span
                  className={`ml-2 inline-flex items-center justify-center rounded-full text-[11px] font-bold px-1.5 min-w-[1.25rem] ${
                    pendientesCounts.needAttention > 0
                      ? 'bg-red-100 text-red-700'
                      : 'bg-azul-surelia/10 text-azul-surelia'
                  }`}
                >
                  {pendientesCounts.total}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Buscador — filtra la pestaña activa mientras se escribe */}
      <div className="relative max-w-md">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por cliente, contrato, fianza o tipo..."
          className="w-full rounded-lg border border-azul-surelia/20 bg-white py-2 pl-9 pr-9 text-sm text-azul-surelia placeholder:text-gray-400 focus:border-azul-surelia focus:outline-none focus:ring-2 focus:ring-azul-surelia/30"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-azul-surelia"
            title="Limpiar búsqueda"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Text className="text-gray-500">Cargando datos...</Text>
        </div>
      )}

      {/* Tab Panels */}
      {!loading && (
        <>
          {/* Clientes Tab */}
          {activeTab === 'clientes' && (
            <div className="space-y-4">
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div
                  className={`bg-green-50 border border-green-200 rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow ${clientFilter === 'ok' ? 'ring-2 ring-green-500' : ''}`}
                  onClick={() => setClientFilter(clientFilter === 'ok' ? null : 'ok')}
                >
                  <div className="text-2xl font-bold text-green-700">{clientCounts.ok}</div>
                  <div className="text-sm text-green-600">OK</div>
                </div>
                <div
                  className={`bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow ${clientFilter === 'porVencer' ? 'ring-2 ring-yellow-500' : ''}`}
                  onClick={() => setClientFilter(clientFilter === 'porVencer' ? null : 'porVencer')}
                >
                  <div className="text-2xl font-bold text-yellow-700">{clientCounts.porVencer}</div>
                  <div className="text-sm text-yellow-600">Por Vencer</div>
                </div>
                <div
                  className={`bg-red-50 border border-red-200 rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow ${clientFilter === 'vencidos' ? 'ring-2 ring-red-500' : ''}`}
                  onClick={() => setClientFilter(clientFilter === 'vencidos' ? null : 'vencidos')}
                >
                  <div className="text-2xl font-bold text-red-700">{clientCounts.vencidos}</div>
                  <div className="text-sm text-red-600">Vencidos</div>
                </div>
                <div
                  className={`bg-gray-50 border border-gray-200 rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow ${clientFilter === 'incompletos' ? 'ring-2 ring-gray-500' : ''}`}
                  onClick={() => setClientFilter(clientFilter === 'incompletos' ? null : 'incompletos')}
                >
                  <div className="text-2xl font-bold text-gray-700">{clientCounts.incompletos}</div>
                  <div className="text-sm text-gray-600">Incompletos</div>
                </div>
              </div>

              {/* Client list */}
              {clientCounts.total > 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-azul-surelia">
                        <tr>
                          <th className="w-10 px-2 py-3"></th>
                          <th className="text-left px-4 py-3 font-medium text-white">Cliente</th>
                          <th className="text-left px-4 py-3 font-medium text-white">RFC</th>
                          <th className="text-left px-4 py-3 font-medium text-white">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredClients().map((client) => {
                          const isExpanded = expandedClients.has(client.id);
                          return (
                            <React.Fragment key={client.id}>
                              <tr
                                className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                                onClick={(e) => toggleClientExpanded(client.id, e)}
                              >
                                <td className="px-2 py-3 text-center">
                                  <button className="p-1 hover:bg-gray-200 rounded">
                                    {isExpanded ? (
                                      <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                                    ) : (
                                      <ChevronRightIcon className="w-4 h-4 text-gray-500" />
                                    )}
                                  </button>
                                </td>
                                <td className="px-4 py-3">
                                  <span
                                    className="font-medium text-azul-surelia hover:underline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(`/app/clientes/${client.id}`, '_blank');
                                    }}
                                  >
                                    {client.nombreEmpresa || 'Sin nombre'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-gray-600 font-mono text-xs">{client.rfc || 'N/A'}</td>
                                <td className="px-4 py-3">
                                  <div className="flex flex-wrap gap-1">
                                    {client.isIncompleto && (
                                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                        Incompleto
                                      </span>
                                    )}
                                    {client.isVencido && (
                                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                        Vencido
                                      </span>
                                    )}
                                    {client.isPorVencer && (
                                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                        Por Vencer
                                      </span>
                                    )}
                                    {!client.isIncompleto && !client.isVencido && !client.isPorVencer && (
                                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                        OK
                                      </span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {/* Expanded row with document status */}
                              {isExpanded && (
                                <tr className="bg-gray-50">
                                  <td colSpan={4} className="px-4 py-3">
                                    {client.requestedDocs?.length > 0 ? (
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
                                      {client.requestedDocs.map((docType) => {
                                        const status = getDocStatus(client, docType);
                                        return (
                                          <div
                                            key={docType}
                                            className={`flex items-center gap-2 p-2 rounded-lg border ${
                                              status === 'ok' ? 'bg-green-50 border-green-200' :
                                              status === 'expiring' ? 'bg-yellow-50 border-yellow-200' :
                                              status === 'expired' ? 'bg-red-50 border-red-200' :
                                              'bg-gray-100 border-gray-300'
                                            }`}
                                          >
                                            {status === 'ok' && <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />}
                                            {status === 'expiring' && <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 flex-shrink-0" />}
                                            {status === 'expired' && <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0" />}
                                            {status === 'missing' && <XCircleIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />}
                                            <div className="min-w-0">
                                              <p className={`text-xs font-medium truncate ${
                                                status === 'ok' ? 'text-green-700' :
                                                status === 'expiring' ? 'text-yellow-700' :
                                                status === 'expired' ? 'text-red-700' :
                                                'text-gray-600'
                                              }`}>
                                                {DOC_LABELS[docType] || docType}
                                              </p>
                                              <p className={`text-[10px] ${
                                                status === 'ok' ? 'text-green-600' :
                                                status === 'expiring' ? 'text-yellow-600' :
                                                status === 'expired' ? 'text-red-600' :
                                                'text-gray-500'
                                              }`}>
                                                {status === 'ok' ? 'Completo' :
                                                 status === 'expiring' ? 'Por vencer' :
                                                 status === 'expired' ? 'Vencido' :
                                                 'Faltante'}
                                              </p>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                    ) : (
                                      <p className="text-sm text-gray-400 italic">Sin documentos solicitados</p>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <Text className="text-xs text-gray-400 text-center italic">
                  No hay clientes registrados
                </Text>
              )}
            </div>
          )}

          {/* Pipeline Tab */}
          {activeTab === 'pipeline' && (
            <div className="space-y-4">
              {/* Diferidas indicator */}
              {pipelineCounts.diferidas > 0 && (
                <div
                  className={`flex items-center justify-between px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                    pipelineCounts.diferidasNeedAttention > 0
                      ? 'bg-red-50 border border-red-200 hover:bg-red-100'
                      : 'bg-amber-50 border border-amber-200 hover:bg-amber-100'
                  }`}
                  onClick={() => setActiveTab('diferidas')}
                >
                  <div className="flex items-center gap-2">
                    <PauseCircleIcon className={`w-5 h-5 ${
                      pipelineCounts.diferidasNeedAttention > 0 ? 'text-red-600' : 'text-amber-600'
                    }`} />
                    <span className={`text-sm font-medium ${
                      pipelineCounts.diferidasNeedAttention > 0 ? 'text-red-700' : 'text-amber-700'
                    }`}>
                      {pipelineCounts.diferidas} fianza{pipelineCounts.diferidas !== 1 ? 's' : ''} diferida{pipelineCounts.diferidas !== 1 ? 's' : ''}
                      {pipelineCounts.diferidasNeedAttention > 0 && (
                        <span className="text-red-700 font-semibold"> ({pipelineCounts.diferidasNeedAttention} requiere{pipelineCounts.diferidasNeedAttention !== 1 ? 'n' : ''} atención)</span>
                      )}
                    </span>
                  </div>
                  <ChevronRightIcon className={`w-4 h-4 ${
                    pipelineCounts.diferidasNeedAttention > 0 ? 'text-red-500' : 'text-amber-500'
                  }`} />
                </div>
              )}

              {/* Source filter */}
              <div className="flex items-center gap-2">
                <Text className="text-xs text-gray-500">Mostrar:</Text>
                {[
                  { key: 'all', label: 'Todos' },
                  { key: 'clientes', label: 'Clientes' },
                  { key: 'convenios', label: 'Convenios' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSourceFilter(opt.key)}
                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                      sourceFilter === opt.key
                        ? opt.key === 'convenios'
                          ? 'bg-[#53B5CC]/15 text-[#53B5CC] border border-[#53B5CC]/30'
                          : 'bg-azul-surelia/10 text-azul-surelia border border-azul-surelia/30'
                        : 'bg-gray-100 text-gray-500 border border-transparent hover:bg-gray-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={collisionDetection}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
              >
                <div className="flex gap-4 overflow-x-auto pb-4 min-h-[500px]">
                {PIPELINE_COLUMNS.map((column) => (
                  <PipelineColumn
                    key={column.id}
                    columnId={column.id}
                    title={column.title}
                    subtitle={column.subtitle}
                    items={filteredPipeline[column.id] || []}
                    onCardClick={handleCardClick}
                    onDiferir={handleDiferir}
                    onReactivar={handleReactivar}
                    onEditEnProceso={handleEditEnProceso}
                  />
                ))}
              </div>

                {/* Drag overlay */}
                {createPortal(
                  <DragOverlay dropAnimation={null}>
                    {activeCard ? (
                      <PipelineCard item={activeCard} isDragging />
                    ) : null}
                  </DragOverlay>,
                  document.body
                )}
              </DndContext>
            </div>
          )}

          {/* Diferidas Tab */}
          {activeTab === 'diferidas' && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center gap-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-center">
                  <div className="text-2xl font-bold text-amber-700">{pipelineCounts.diferidas}</div>
                  <div className="text-sm text-amber-600">Total diferidas</div>
                </div>
                {pipelineCounts.diferidasNeedAttention > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-center">
                    <div className="text-2xl font-bold text-red-700">{pipelineCounts.diferidasNeedAttention}</div>
                    <div className="text-sm text-red-600">Requieren atención</div>
                  </div>
                )}
              </div>

              {/* Diferidas list */}
              {diferidasFiltradas.length > 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-azul-surelia">
                        <tr>
                          <th className="text-left px-4 py-3 font-medium text-white">Cliente</th>
                          <th className="text-left px-4 py-3 font-medium text-white">Contrato</th>
                          <th className="text-left px-4 py-3 font-medium text-white">Tipo Fianza</th>
                          <th className="text-left px-4 py-3 font-medium text-white">Fin Contrato</th>
                          <th className="text-right px-4 py-3 font-medium text-white">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diferidasFiltradas.map((item, idx) => (
                          <tr
                            key={item.id || idx}
                            className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                            onClick={() => setSelectedContract(item)}
                          >
                            <td className="px-4 py-3">
                              <span className="font-medium text-azul-surelia">{item.clientName}</span>
                            </td>
                            <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate" title={item.contractTitle}>
                              {item.contractTitle}
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {item.tipo || 'Sin tipo'}
                            </td>
                            <td className="px-4 py-3">
                              {item.fechaTerminoStr ? (
                                <div>
                                  <span className="text-gray-600">{item.fechaTerminoStr}</span>
                                  {item.daysUntilContractEnd !== undefined && (
                                    <span className={`block text-xs ${
                                      item.daysUntilContractEnd < 0 ? 'text-red-600' :
                                      item.daysUntilContractEnd <= 30 ? 'text-amber-600' :
                                      'text-gray-500'
                                    }`}>
                                      {item.daysUntilContractEnd < 0
                                        ? `Venció hace ${Math.abs(item.daysUntilContractEnd)} días`
                                        : `${item.daysUntilContractEnd} días restantes`}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400">N/A</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                className="text-sm text-azul-surelia hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReactivar(item);
                                }}
                              >
                                Reactivar
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <PauseCircleIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <Text className="text-sm">No hay fianzas diferidas</Text>
                </div>
              )}
            </div>
          )}

          {/* Después del Término Tab */}
          {activeTab === 'despues_termino' && (
            <div className="space-y-4">
              {/* Summary cards */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="bg-azul-surelia/5 border border-azul-surelia/20 rounded-lg px-4 py-3 text-center">
                  <div className="text-2xl font-bold text-azul-surelia">{despuesTerminoCounts.total}</div>
                  <div className="text-sm text-azul-surelia/70">Total</div>
                </div>
                {despuesTerminoCounts.needsAttention > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-center">
                    <div className="text-2xl font-bold text-green-700">{despuesTerminoCounts.needsAttention}</div>
                    <div className="text-sm text-green-600">Listas para tramitar</div>
                  </div>
                )}
                {despuesTerminoCounts.diferidas > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-center">
                    <div className="text-2xl font-bold text-amber-700">{despuesTerminoCounts.diferidas}</div>
                    <div className="text-sm text-amber-600">Diferidas</div>
                  </div>
                )}
              </div>

              {/* Table */}
              {despuesTerminoFiltrado.length > 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-azul-surelia">
                        <tr>
                          <th className="w-10 px-2 py-3"></th>
                          <th className="text-left px-4 py-3 font-medium text-white">Cliente</th>
                          <th className="text-left px-4 py-3 font-medium text-white">Contrato</th>
                          <th className="text-left px-4 py-3 font-medium text-white">Tipo Fianza</th>
                          <th className="text-left px-4 py-3 font-medium text-white">Estado</th>
                          <th className="text-left px-4 py-3 font-medium text-white">Última Vigencia Hermanas</th>
                          <th className="text-right px-4 py-3 font-medium text-white">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {despuesTerminoFiltrado.map((item, idx) => {
                          const isExpanded = expandedDespuesTermino.has(item.id);
                          const estatus = item.estatus || 'pendiente';
                          return (
                            <React.Fragment key={item.id || idx}>
                              <tr
                                className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${item.needsAttention ? 'bg-green-50/50' : ''}`}
                                onClick={() => setSelectedContract(item)}
                              >
                                <td className="px-2 py-3 text-center">
                                  <button
                                    className="p-1 hover:bg-gray-200 rounded"
                                    onClick={(e) => toggleDespuesTerminoExpanded(item.id, e)}
                                  >
                                    {isExpanded ? (
                                      <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                                    ) : (
                                      <ChevronRightIcon className="w-4 h-4 text-gray-500" />
                                    )}
                                  </button>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="font-medium text-azul-surelia">{item.clientName}</span>
                                </td>
                                <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate" title={item.contractTitle}>
                                  {item.contractTitle}
                                </td>
                                <td className="px-4 py-3 text-gray-700">
                                  {item.tipo || 'Sin tipo'}
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                    estatus === 'diferida' ? 'bg-amber-100 text-amber-700' :
                                    estatus === 'en_proceso' ? 'bg-blue-100 text-blue-700' :
                                    'bg-orange-100 text-orange-700'
                                  }`}>
                                    {estatus === 'diferida' ? 'Diferida' :
                                     estatus === 'en_proceso' ? 'En Proceso' :
                                     'Pendiente'}
                                  </span>
                                  {item.needsAttention && (
                                    <span className="inline-block ml-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                      Lista
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  {item.latestSiblingExpiry ? (
                                    <div>
                                      <span className="text-gray-600">
                                        {new Date(item.latestSiblingExpiry).toLocaleDateString('es-MX')}
                                      </span>
                                      {item.latestSiblingExpiryDays != null && (
                                        <span className={`block text-xs ${
                                          item.latestSiblingExpiryDays < 0 ? 'text-red-600' :
                                          item.latestSiblingExpiryDays <= 30 ? 'text-amber-600' :
                                          'text-gray-500'
                                        }`}>
                                          {item.latestSiblingExpiryDays < 0
                                            ? `Venció hace ${Math.abs(item.latestSiblingExpiryDays)} días`
                                            : `${item.latestSiblingExpiryDays} días restantes`}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">Sin vigencia</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  {estatus === 'diferida' ? (
                                    <button
                                      className="text-sm text-azul-surelia hover:underline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleReactivar(item);
                                      }}
                                    >
                                      Reactivar
                                    </button>
                                  ) : (
                                    <button
                                      className="text-sm text-amber-600 hover:underline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDiferir(item);
                                      }}
                                    >
                                      Diferir
                                    </button>
                                  )}
                                </td>
                              </tr>
                              {/* Expanded row: sibling fianzas */}
                              {isExpanded && (
                                <tr className="bg-gray-50">
                                  <td colSpan={7} className="px-6 py-3">
                                    {item.siblings.length > 0 ? (
                                      <div>
                                        <p className="text-xs font-medium text-gray-500 mb-2">Fianzas hermanas en este contrato</p>
                                        <table className="w-full text-xs">
                                          <thead>
                                            <tr className="text-gray-500">
                                              <th className="text-left py-1 pr-4">Tipo</th>
                                              <th className="text-left py-1 pr-4">Momento</th>
                                              <th className="text-left py-1 pr-4">Estado</th>
                                              <th className="text-left py-1">Vencimiento</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {item.siblings.map((sibling, sIdx) => (
                                              <tr key={sIdx} className="border-t border-gray-200">
                                                <td className="py-1.5 pr-4 text-gray-700">{sibling.tipo}</td>
                                                <td className="py-1.5 pr-4 text-gray-500">
                                                  {sibling.momento === 'despues_termino' ? 'Después del término' : 'Antes del inicio'}
                                                </td>
                                                <td className="py-1.5 pr-4">
                                                  <span className={`inline-block px-2 py-0.5 rounded-full font-medium ${
                                                    sibling.status === 'pagada' ? 'bg-green-100 text-green-700' :
                                                    sibling.status === 'emitida' ? 'bg-blue-100 text-blue-700' :
                                                    sibling.status === 'diferida' ? 'bg-gray-100 text-gray-600' :
                                                    'bg-orange-100 text-orange-700'
                                                  }`}>
                                                    {sibling.status === 'pagada' ? 'Pagada' :
                                                     sibling.status === 'emitida' ? 'Emitida' :
                                                     sibling.status === 'diferida' ? 'Diferida' :
                                                     sibling.status === 'en_proceso' ? 'En Proceso' :
                                                     'Pendiente'}
                                                  </span>
                                                </td>
                                                <td className="py-1.5">
                                                  {sibling.finVigencia ? (
                                                    <div>
                                                      <span className="text-gray-600">
                                                        {new Date(sibling.finVigencia).toLocaleDateString('es-MX')}
                                                      </span>
                                                      {sibling.daysUntilExpiry != null && (
                                                        <span className={`ml-1 ${
                                                          sibling.daysUntilExpiry < 0 ? 'text-red-600' :
                                                          sibling.daysUntilExpiry <= 30 ? 'text-amber-600' :
                                                          'text-gray-400'
                                                        }`}>
                                                          ({sibling.daysUntilExpiry < 0
                                                            ? `${Math.abs(sibling.daysUntilExpiry)}d vencida`
                                                            : `${sibling.daysUntilExpiry}d`})
                                                        </span>
                                                      )}
                                                    </div>
                                                  ) : (
                                                    <span className="text-gray-400">-</span>
                                                  )}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <p className="text-xs text-gray-400 italic">Sin fianzas hermanas en este contrato</p>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Text className="text-sm">No hay fianzas después del término</Text>
                </div>
              )}
            </div>
          )}

          {/* Vencimientos Tab */}
          {activeTab === 'vencimientos' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div
                  className={`bg-purple-50 border rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow ${
                    expirationFilters.vencidas ? 'border-purple-500 ring-2 ring-purple-300' : 'border-purple-200'
                  }`}
                  onClick={() => toggleExpirationFilter('vencidas')}
                >
                  <div className="text-2xl font-bold text-purple-700">{expirationCounts.vencidas}</div>
                  <div className="text-sm text-purple-600">Vencidas</div>
                </div>
                <div
                  className={`bg-red-50 border rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow ${
                    expirationFilters.under30 ? 'border-red-500 ring-2 ring-red-300' : 'border-red-200'
                  }`}
                  onClick={() => toggleExpirationFilter('under30')}
                >
                  <div className="text-2xl font-bold text-red-700">{expirationCounts.under30}</div>
                  <div className="text-sm text-red-600">&lt; 30 dias</div>
                </div>
                <div
                  className={`bg-yellow-50 border rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow ${
                    expirationFilters.under60 ? 'border-yellow-500 ring-2 ring-yellow-300' : 'border-yellow-200'
                  }`}
                  onClick={() => toggleExpirationFilter('under60')}
                >
                  <div className="text-2xl font-bold text-yellow-700">{expirationCounts.under60}</div>
                  <div className="text-sm text-yellow-600">&lt; 60 dias</div>
                </div>
                <div
                  className={`bg-blue-50 border rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow ${
                    expirationFilters.under90 ? 'border-blue-500 ring-2 ring-blue-300' : 'border-blue-200'
                  }`}
                  onClick={() => toggleExpirationFilter('under90')}
                >
                  <div className="text-2xl font-bold text-blue-700">{expirationCounts.under90}</div>
                  <div className="text-sm text-blue-600">&lt; 90 dias</div>
                </div>
                <div
                  className={`bg-gray-50 border rounded-lg p-4 text-center cursor-pointer hover:shadow-md transition-shadow ${
                    expirationFilters.over90 ? 'border-gray-500 ring-2 ring-gray-300' : 'border-gray-200'
                  }`}
                  onClick={() => toggleExpirationFilter('over90')}
                >
                  <div className="text-2xl font-bold text-gray-700">{expirationCounts.over90}</div>
                  <div className="text-sm text-gray-600">&gt; 90 dias</div>
                </div>
              </div>

              {/* Renovable filter */}
              <div className="flex items-center justify-end">
                <label className="flex items-center gap-2 cursor-pointer bg-white border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={showRenovableOnly}
                    onChange={(e) => setShowRenovableOnly(e.target.checked)}
                    className="w-4 h-4 text-azul-surelia border-gray-300 rounded focus:ring-azul-surelia"
                  />
                  <span className="text-sm text-gray-700">Solo renovables</span>
                </label>
              </div>

              {/* Expiration list */}
              {getFilteredExpirations().length > 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-azul-surelia">
                        <tr>
                          <th className="text-left px-4 py-3 font-medium text-white">Cliente</th>
                          <th className="text-left px-4 py-3 font-medium text-white">Contrato</th>
                          <th className="text-left px-4 py-3 font-medium text-white">Prima</th>
                          <th className="text-left px-4 py-3 font-medium text-white">Vencimiento</th>
                          <th className="text-left px-4 py-3 font-medium text-white">Estado</th>
                          <th className="text-left px-4 py-3 font-medium text-white">Renovable</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredExpirations()
                          .slice(0, 50)
                          .map((fianza, idx) => (
                            <tr
                              key={fianza.id || idx}
                              className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                              onClick={() => setSelectedFianza(fianza)}
                            >
                              <td className="px-4 py-3">
                                <span className="font-medium text-azul-surelia">{fianza.clientName}</span>
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className="text-gray-600 hover:text-azul-surelia hover:underline max-w-[200px] truncate block"
                                  title={fianza.contractTitle}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedContract(fianza);
                                  }}
                                >
                                  {fianza.contractTitle}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-gray-800 font-medium">
                                {fianza.prima
                                  ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(fianza.prima)
                                  : 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {fianza.finVigenciaFianza
                                  ? new Date(fianza.finVigenciaFianza).toLocaleDateString('es-MX')
                                  : 'N/A'}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                  fianza.daysUntilExpiry < 0 ? 'bg-purple-100 text-purple-700' :
                                  fianza.daysUntilExpiry <= 30 ? 'bg-red-100 text-red-700' :
                                  fianza.daysUntilExpiry <= 60 ? 'bg-yellow-100 text-yellow-700' :
                                  fianza.daysUntilExpiry <= 90 ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {fianza.daysUntilExpiry < 0
                                    ? `Vencida hace ${Math.abs(fianza.daysUntilExpiry)} dias`
                                    : `${fianza.daysUntilExpiry} dias`}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {fianza.renovable ? (
                                  <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-verde-lima text-azul-surelia">
                                    Sí
                                  </span>
                                ) : (
                                  <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                                    No
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <Text className="text-xs text-gray-400 text-center italic">
                  {expirationCounts.total > 0
                    ? 'Selecciona una categoría para ver las fianzas'
                    : 'No hay fianzas pagadas con vencimiento registrado'}
                </Text>
              )}
            </div>
          )}

          {/* Endosos Pendientes Tab — endosos the agente was asked for and has
              not applied yet. Purely pull-based: the reminder is this list. */}
          {activeTab === 'pendientes' && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="bg-azul-surelia/5 border border-azul-surelia/20 rounded-lg px-4 py-3 text-center">
                  <div className="text-2xl font-bold text-azul-surelia">{pendientesCounts.total}</div>
                  <div className="text-sm text-azul-surelia/70">Abiertos</div>
                </div>
                {pendientesCounts.vencidos > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-center">
                    <div className="text-2xl font-bold text-red-700">{pendientesCounts.vencidos}</div>
                    <div className="text-sm text-red-600">Fuera de fecha</div>
                  </div>
                )}
                {pendientesCounts.urgentes > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-center">
                    <div className="text-2xl font-bold text-amber-700">{pendientesCounts.urgentes}</div>
                    <div className="text-sm text-amber-600">Vencen esta semana</div>
                  </div>
                )}
                {pendientesCounts.sinFecha > 0 && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-center">
                    <div className="text-2xl font-bold text-gray-600">{pendientesCounts.sinFecha}</div>
                    <div className="text-sm text-gray-500">Sin fecha límite</div>
                  </div>
                )}
                <button
                  onClick={() => navigate('/app/pendientes')}
                  className="ml-auto text-sm font-medium text-azul-surelia hover:underline"
                >
                  Abrir Pendientes
                </button>
              </div>

              {pendientesFiltrados.length > 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-azul-surelia">
                        <tr>
                          <th className="text-left px-4 py-3 font-medium text-white">Cliente</th>
                          <th className="text-left px-4 py-3 font-medium text-white">Pendiente</th>
                          <th className="text-left px-4 py-3 font-medium text-white">Afianzadora</th>
                          <th className="text-left px-4 py-3 font-medium text-white">Fecha límite</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendientesFiltrados.map((item) => {
                          const tono = item.urgencia === 'vencido'
                            ? 'text-red-700 font-semibold'
                            : item.urgencia === 'urgente'
                            ? 'text-amber-700 font-semibold'
                            : 'text-gray-600';
                          const esEndoso = item.origen === 'endoso';
                          // Endoso rows carry the fianza's tipos; collection
                          // rows carry a title the agente typed.
                          const queFalta = esEndoso && item.tipos?.length
                            ? item.tipos.map((t) => EVENTO_TIPO_LABELS[t] || t).join(' + ')
                            : item.titulo;
                          const clickable = Boolean(item.contractId && contractsById[item.contractId]);
                          return (
                            <tr
                              key={item.id}
                              className={`border-b border-gray-100 ${
                                clickable ? 'hover:bg-gray-50 cursor-pointer' : ''
                              }`}
                              onClick={() => clickable && setSelectedContract(item)}
                            >
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-800">{item.clientName}</div>
                                <div className="text-xs text-gray-400">{item.contractTitle}</div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-gray-800">{queFalta}</div>
                                <div className="text-xs text-gray-500">
                                  {PENDIENTE_TIPO_LABELS[item.tipo] || item.tipo}
                                  {esEndoso && item.numeroFianza
                                    ? ` — fianza #${item.numeroFianza}`
                                    : ''}
                                  {esEndoso && item.solicitadoPor
                                    ? ` · pidió ${ENDOSO_SOLICITANTE_LABELS[item.solicitadoPor] || item.solicitadoPor}`
                                    : ''}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {item.afianzadora || '—'}
                              </td>
                              <td className={`px-4 py-3 ${tono}`}>
                                {item.fechaLimite
                                  ? new Date(Number(item.fechaLimite)).toLocaleDateString('es-MX')
                                  : '—'}
                                {item.diasRestantes != null && (
                                  <div className="text-xs font-normal">
                                    {item.diasRestantes < 0
                                      ? `${Math.abs(item.diasRestantes)} días de retraso`
                                      : `en ${item.diasRestantes} días`}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                  <Text className="text-gray-500">No hay pendientes abiertos.</Text>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ContratoView Modal - open the related contract */}
      {selectedContract && contractsById[selectedContract.contractId] && createPortal(
        <ContratoView
          isOpen={!!selectedContract}
          onClose={() => setSelectedContract(null)}
          contrato={contractsById[selectedContract.contractId]}
          clientData={clientsById[selectedContract.clientId]}
        />,
        document.body
      )}

      {/* FianzaView Modal - open fianza details */}
      {selectedFianza && createPortal(
        <FianzaView
          isOpen={!!selectedFianza}
          onClose={() => setSelectedFianza(null)}
          fianza={selectedFianza}
          clientData={clientsById[selectedFianza.clientId]}
          contratoData={contractsById[selectedFianza.contractId]}
        />,
        document.body
      )}

      {/* Diferir Dialog */}
      {postergarItem && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDiferirItem(null)}
          />
          {/* Dialog */}
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Diferir Fianza</h3>
              <p className="text-sm text-gray-500">{postergarItem.tipo || 'Sin tipo'} - {postergarItem.clientName}</p>
            </div>

            {/* Contract expiration info */}
            {postergarItem.fechaTerminoStr && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-500">Fin del Contrato</p>
                <p className="text-sm font-medium text-gray-900">{postergarItem.fechaTerminoStr}</p>
                {postergarItem.daysUntilContractEnd !== undefined && (
                  <p className={`text-xs mt-0.5 ${
                    postergarItem.daysUntilContractEnd < 0 ? 'text-red-600' :
                    postergarItem.daysUntilContractEnd <= 30 ? 'text-amber-600' :
                    'text-gray-500'
                  }`}>
                    {postergarItem.daysUntilContractEnd < 0
                      ? `Venció hace ${Math.abs(postergarItem.daysUntilContractEnd)} días`
                      : `${postergarItem.daysUntilContractEnd} días restantes`}
                  </p>
                )}
              </div>
            )}

            <p className="text-sm text-gray-600">
              Serás notificado oportunamente.
            </p>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                onClick={() => setDiferirItem(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600 transition-colors"
                onClick={handleDiferirConfirm}
              >
                Diferir
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* En Proceso Dialog */}
      {enProcesoItem && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setEnProcesoItem(null);
              setEnProcesoAfianzadoras([{ nombre: '', comentarios: '' }]);
            }}
          />
          {/* Dialog */}
          <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Iniciar Proceso</h3>
              <p className="text-sm text-gray-500">{enProcesoItem.tipo || 'Sin tipo'} - {enProcesoItem.clientName}</p>
            </div>

            {/* Afianzadoras list */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Afianzadoras <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <button
                  type="button"
                  onClick={() => setEnProcesoAfianzadoras(prev => [...prev, { nombre: '', comentarios: '' }])}
                  className="text-xs text-azul-surelia hover:text-azul-surelia/80 font-medium"
                >
                  + Agregar afianzadora
                </button>
              </div>

              {enProcesoAfianzadoras.length === 0 ? (
                <p className="text-sm text-gray-500 italic py-3">
                  Sin afianzadoras. Puedes agregar una o continuar sin ellas.
                </p>
              ) : (
                <div className="space-y-3">
                  {enProcesoAfianzadoras.map((afianzadora, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 space-y-2">
                          <input
                            type="text"
                            value={afianzadora.nombre}
                            onChange={(e) => {
                              const newList = [...enProcesoAfianzadoras];
                              newList[idx].nombre = e.target.value;
                              setEnProcesoAfianzadoras(newList);
                            }}
                            placeholder="Nombre de la afianzadora"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-azul-surelia/30 focus:border-azul-surelia outline-none"
                          />
                          <textarea
                            value={afianzadora.comentarios}
                            onChange={(e) => {
                              const newList = [...enProcesoAfianzadoras];
                              newList[idx].comentarios = e.target.value;
                              setEnProcesoAfianzadoras(newList);
                            }}
                            placeholder="Comentarios sobre esta afianzadora..."
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-azul-surelia/30 focus:border-azul-surelia outline-none resize-none"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newList = enProcesoAfianzadoras.filter((_, i) => i !== idx);
                            setEnProcesoAfianzadoras(newList);
                          }}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                          title="Eliminar"
                        >
                          <XCircleIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                onClick={() => {
                  setEnProcesoItem(null);
                  setEnProcesoAfianzadoras([{ nombre: '', comentarios: '' }]);
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-white bg-azul-surelia rounded-lg hover:bg-azul-surelia/90 transition-colors"
                onClick={handleEnProcesoConfirm}
              >
                Iniciar Proceso
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit En Proceso Dialog */}
      {editEnProcesoItem && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setEditEnProcesoItem(null);
              setEnProcesoAfianzadoras([{ nombre: '', comentarios: '' }]);
            }}
          />
          {/* Dialog */}
          <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Editar Proceso</h3>
              <p className="text-sm text-gray-500">{editEnProcesoItem.tipo || 'Sin tipo'} - {editEnProcesoItem.clientName}</p>
            </div>

            {/* Afianzadoras list */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Afianzadoras <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <button
                  type="button"
                  onClick={() => setEnProcesoAfianzadoras(prev => [...prev, { nombre: '', comentarios: '' }])}
                  className="text-xs text-azul-surelia hover:text-azul-surelia/80 font-medium"
                >
                  + Agregar afianzadora
                </button>
              </div>

              {enProcesoAfianzadoras.length === 0 ? (
                <p className="text-sm text-gray-500 italic py-3">
                  Sin afianzadoras. Puedes agregar una o guardar sin ellas.
                </p>
              ) : (
                <div className="space-y-3">
                  {enProcesoAfianzadoras.map((afianzadora, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 space-y-2">
                          <input
                            type="text"
                            value={afianzadora.nombre}
                            onChange={(e) => {
                              const newList = [...enProcesoAfianzadoras];
                              newList[idx].nombre = e.target.value;
                              setEnProcesoAfianzadoras(newList);
                            }}
                            placeholder="Nombre de la afianzadora"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-azul-surelia/30 focus:border-azul-surelia outline-none"
                          />
                          <textarea
                            value={afianzadora.comentarios}
                            onChange={(e) => {
                              const newList = [...enProcesoAfianzadoras];
                              newList[idx].comentarios = e.target.value;
                              setEnProcesoAfianzadoras(newList);
                            }}
                            placeholder="Comentarios sobre esta afianzadora..."
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-azul-surelia/30 focus:border-azul-surelia outline-none resize-none"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newList = enProcesoAfianzadoras.filter((_, i) => i !== idx);
                            setEnProcesoAfianzadoras(newList);
                          }}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                          title="Eliminar"
                        >
                          <XCircleIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                onClick={() => {
                  setEditEnProcesoItem(null);
                  setEnProcesoAfianzadoras([{ nombre: '', comentarios: '' }]);
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-white bg-azul-surelia rounded-lg hover:bg-azul-surelia/90 transition-colors"
                onClick={handleEditEnProcesoConfirm}
              >
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Pulso;
